import React, { useEffect, useState } from "react";
import { FaGithub, FaExternalLinkAlt, FaArrowRight } from "react-icons/fa";
import { Link, useSearchParams } from "react-router-dom";
import SEO from "../components/SEO";
import PageHero from "../components/page/PageHero";
import SectionHeading from "../components/page/SectionHeading";
import STM32SuitePage from "./openSource/libraries/stm32/STM32SuitePage";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "react-i18next";
import {
  OPEN_SOURCE_CATEGORIES,
  itemsByCategory,
} from "../data/openSourceItems";

const OpenSource = () => {
  const { t } = useTranslation();

  // Tab lives in the URL (?tab=tools). This is the fix for the old flow: return
  // from a tool page and you land back on the tab you were on, not the default.
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = OPEN_SOURCE_CATEGORIES.map((c) => c.id);
  const requestedTab = searchParams.get("tab");
  const activeTab = validTabs.includes(requestedTab) ? requestedTab : "research";

  const setActiveTab = (id) => {
    setSearchParams(id === "research" ? {} : { tab: id }, { replace: true });
  };

  const groups = itemsByCategory();
  const activeGroup = groups.find((g) => g.id === activeTab);

  const [contentRef, contentInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [ctaRef, ctaInView] = useInView({ triggerOnce: true, threshold: 0.15, rootMargin: "-50px" });

  // Belt-and-braces reveal in case the observer misfires on some devices.
  const [fallbackVisible, setFallbackVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setFallbackVisible(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="bg-black font-sans min-h-screen">
      <SEO
        title="Open Source Tools & Research | Riviera Tech Solutions"
        description="Free open source tools, calculators, and research projects by Riviera Tech Solutions. Includes an electronics toolkit, Gantt chart generator, embedded C libraries, and open engineering designs."
        keywords="open source tools, free electronics calculator, Ohm's law calculator, gantt chart generator, open source research, embedded c libraries"
        url="/open-source"
        type="website"
      />

      <PageHero
        eyebrow={t("openSource.hero.eyebrow", "Open Source")}
        title={t("openSource.hero.title", "Tools, research and designs — free to use and build on")}
        subtitle={t("openSource.hero.subtitle", "A growing collection of things we built and gave away: browser calculators, an AI project planner, embedded libraries, and open engineering designs. No sign-up, no catch.")}
        ctaLabel={t("openSource.hero.cta", "Browse the tools")}
        ctaTo="/open-source?tab=tools"
        align="center"
      />

      {/* Tabs — driven by the manifest and reflected in the URL */}
      <section className="bg-black border-b border-gray-800">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2 py-4">
            {OPEN_SOURCE_CATEGORIES.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 rounded-lg px-4 md:px-6 py-2.5 text-sm font-medium transition-all duration-300 ${isActive
                    ? "bg-moss/15 text-moss-light border border-moss/40"
                    : "text-silver-medium hover:text-silver-light hover:bg-white/5 border border-transparent"
                    }`}
                  aria-current={isActive ? "true" : undefined}
                >
                  <Icon className="text-base" aria-hidden="true" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Content */}
      <section ref={contentRef} className="py-16 md:py-20 bg-black">
        <div className="container mx-auto px-4">
          <div
            className={`transition-all duration-1000 transform ${(contentInView || fallbackVisible) ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <SectionHeading
              eyebrow={activeGroup?.label}
              title={t(`openSource.sections.${activeTab}.title`, activeGroup?.label || "")}
              lead={t(`openSource.sections.${activeTab}.lead`, "")}
              className="mb-12"
            />

            {/* Libraries tab keeps its rich STM32 sub-page; every other tab is a
                clean card grid straight from the manifest. */}
            {activeTab === "libraries" ? (
              <STM32SuitePage />
            ) : (
              <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-2">
                {activeGroup?.items.map((item) => {
                  const Icon = item.icon;
                  const isExternal = Boolean(item.href);
                  const CardInner = (
                    <>
                      <div className="mb-4 flex items-center justify-between">
                        <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-800 text-moss-light">
                          <Icon className="text-xl" aria-hidden="true" />
                        </span>
                        {isExternal ? (
                          <FaGithub className="text-silver-dark" aria-hidden="true" />
                        ) : (
                          <FaArrowRight className="text-silver-dark transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                        )}
                      </div>
                      <h3 className="mb-2 text-lg font-medium text-silver-light">{item.title}</h3>
                      <p className="text-sm leading-relaxed text-silver-medium">{item.short}</p>
                      <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-moss-light">
                        {isExternal ? t("openSource.viewOnGithub", "View on GitHub") : t("openSource.openTool", "Open tool")}
                        {isExternal && <FaExternalLinkAlt className="text-xs" aria-hidden="true" />}
                      </span>
                    </>
                  );

                  const cardClass =
                    "group block rounded-lg border border-gray-800 bg-off-black p-6 transition-colors duration-300 hover:border-moss/60";

                  return isExternal ? (
                    <a key={item.id} href={item.href} target="_blank" rel="noopener noreferrer" className={cardClass}>
                      {CardInner}
                    </a>
                  ) : (
                    <Link key={item.id} to={item.to} className={cardClass}>
                      {CardInner}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} className="py-16 md:py-24 bg-black border-t border-gray-800">
        <div className="container mx-auto px-4 text-center">
          <div
            className={`mx-auto max-w-3xl transition-all duration-1000 transform ${(ctaInView || fallbackVisible) ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <h3 className="mb-4 text-2xl md:text-3xl font-serif font-medium text-silver-light">
              {t("openSource.cta.title", "It's all on GitHub")}
            </h3>
            <p className="mb-8 text-silver-medium">
              {t("openSource.cta.description", "Every tool and design here is open source. Use it, fork it, improve it, ship it.")}
            </p>
            <a
              href="https://github.com/Riviera-Tech-Solutions"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-md bg-gray-800 hover:bg-gray-700 px-8 py-4 font-medium text-silver-light transition-all duration-300 border border-gray-700 hover:border-moss/50"
            >
              <FaGithub className="text-2xl" aria-hidden="true" />
              <span>{t("openSource.cta.button", "View our GitHub")}</span>
              <FaExternalLinkAlt className="text-sm" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OpenSource;
